import React from 'react';
import { makeStyles } from '@material-ui/core/styles';
import { FormattedMessage, useIntl } from 'react-intl';
import { Link } from 'react-router-dom';

import HighlightMark from './HighlightMark';
import LandingSection from './LandingSection';
import SectionEyebrow from './SectionEyebrow';
import useStartCta from './useStartCta';
import { REDESIGN_COLORS, REDESIGN_FONTS } from '../../constants';

const useStyles = makeStyles((theme) => ({
    intro: {
        maxWidth: '900px',
        margin: '0 auto',
        textAlign: 'center',
    },
    eyebrowRow: {
        display: 'flex',
        justifyContent: 'center',
        marginBottom: '22px',
    },
    title: {
        margin: '0 0 18px',
        fontSize: 'clamp(2rem, 4.4vw, 3.4rem)',
        lineHeight: 1.1,
        letterSpacing: '-0.035em',
        fontWeight: REDESIGN_FONTS.weightHeading,
        color: REDESIGN_COLORS.dark,
        textWrap: 'balance',
    },
    lead: {
        maxWidth: '680px',
        margin: '0 auto 26px',
        color: REDESIGN_COLORS.body,
        fontSize: '1.06rem',
        lineHeight: 1.6,
    },
    ctaRow: {
        display: 'flex',
        flexWrap: 'wrap',
        justifyContent: 'center',
        gap: '12px',
    },
    cta: {
        display: 'inline-flex',
        alignItems: 'center',
        justifyContent: 'center',
        gap: '10px',
        minHeight: '52px',
        padding: '0 26px',
        borderRadius: '999px',
        fontSize: '1rem',
        fontWeight: REDESIGN_FONTS.weightHeading,
    },
    ctaPrimary: {
        color: REDESIGN_COLORS.white,
        background: REDESIGN_COLORS.primary,
        boxShadow: '0 4px 12px rgba(0, 124, 134, 0.24)',
        '&:hover': {
            color: REDESIGN_COLORS.white,
            background: REDESIGN_COLORS.primaryHover,
        },
    },
    ctaSecondary: {
        border: `1px solid ${REDESIGN_COLORS.line}`,
        color: REDESIGN_COLORS.dark,
        background: REDESIGN_COLORS.white,
        '&:hover': {
            borderColor: REDESIGN_COLORS.primary,
            color: REDESIGN_COLORS.link,
        },
    },
    trust: {
        margin: '16px 0 0',
        color: REDESIGN_COLORS.muted,
        fontSize: '0.92rem',
        fontWeight: 600,
    },
    // The visual sits on the same mint mat and white frame the landing's
    // feature screenshots use, so every page reads as one family.
    figure: {
        boxSizing: 'border-box',
        display: 'grid',
        placeItems: 'center',
        maxWidth: '960px',
        margin: '48px auto 0',
        padding: 'clamp(12px, 1.8vw, 22px)',
        borderRadius: '24px',
        background: REDESIGN_COLORS.mist,
        [theme.breakpoints.down('xs')]: {
            marginTop: '32px',
        },
    },
    frame: {
        boxSizing: 'border-box',
        width: '100%',
        overflow: 'hidden',
        borderRadius: '14px',
        background: REDESIGN_COLORS.white,
        boxShadow: '0 1px 2px rgba(6, 63, 69, 0.06), 0 16px 40px rgba(6, 63, 69, 0.12)',
    },
    // An illustration on a transparent background needs breathing room; a
    // product screenshot fills the frame edge to edge.
    framePadded: {
        padding: 'clamp(16px, 3vw, 40px) clamp(16px, 4vw, 56px)',
    },
    image: {
        display: 'block',
        width: '100%',
        height: 'auto',
        margin: '0 auto',
    },
    imagePadded: {
        maxWidth: '760px',
    },
}));

/**
 * The centred hero the secondary landing pages open with: eyebrow, headline
 * (with optional marker highlight), lead, one or two pill CTAs, a trust line
 * and a framed visual. Copy comes from `${keyPrefix}.eyebrow|title|lead|
 * ctaPrimary|ctaSecondary|trust.<key>|imageAlt`. A "start for free" CTA turns
 * into "see the plans" for a signed-in visitor; if that makes the two CTAs
 * point at the same place, only the primary is shown.
 */
const PageHero = ({ keyPrefix, primaryTo, secondaryTo, trust = [], image, id = 'top' }) => {
    const classes = useStyles();
    const intl = useIntl();

    const t = (key) => intl.formatMessage({ id: `${keyPrefix}.${key}` });
    const primary = useStartCta(primaryTo);
    const secondary = useStartCta(secondaryTo);
    const showSecondary = secondaryTo && secondary.to !== primary.to;

    return (
        <LandingSection id={id} maxWidth={1080} paddingTop={46} paddingBottom={24}>
            <div className={classes.intro}>
                <div className={classes.eyebrowRow}>
                    <SectionEyebrow>
                        <FormattedMessage id={`${keyPrefix}.eyebrow`} />
                    </SectionEyebrow>
                </div>

                <h1 className={classes.title}>
                    <FormattedMessage
                        id={`${keyPrefix}.title`}
                        values={{ mark: (chunks) => <HighlightMark>{chunks}</HighlightMark> }}
                    />
                </h1>

                <p className={classes.lead}>
                    <FormattedMessage id={`${keyPrefix}.lead`} />
                </p>

                <div className={classes.ctaRow}>
                    <Link to={primary.to} className={`${classes.cta} ${classes.ctaPrimary}`}>
                        <FormattedMessage id={primary.labelId || `${keyPrefix}.ctaPrimary`} />
                        <span aria-hidden="true">→</span>
                    </Link>
                    {showSecondary ? (
                        <Link to={secondary.to} className={`${classes.cta} ${classes.ctaSecondary}`}>
                            <FormattedMessage id={secondary.labelId || `${keyPrefix}.ctaSecondary`} />
                        </Link>
                    ) : null}
                </div>

                {trust.length ? (
                    <p className={classes.trust}>{trust.map((key) => t(`trust.${key}`)).join(' · ')}</p>
                ) : null}
            </div>

            {image ? (
                <div className={classes.figure}>
                    <div className={[classes.frame, image.padded ? classes.framePadded : ''].filter(Boolean).join(' ')}>
                        <img
                            src={image.src}
                            alt={t('imageAlt')}
                            width={image.width}
                            height={image.height}
                            className={[classes.image, image.padded ? classes.imagePadded : ''].filter(Boolean).join(' ')}
                        />
                    </div>
                </div>
            ) : null}
        </LandingSection>
    );
};

export default PageHero;
